/*
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Copyright (c) 2020 headwire.com, Inc
  https://github.com/headwirecom/jsonforms-react-spectrum-renderers

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
import {
  Cell,
  Column,
  Row,
  TableView,
  TableBody,
  TableHeader,
  ProgressCircle,
  Flex,
  View,
} from '@adobe/react-spectrum';
import {
  RankedTester,
  rankWith,
  RendererProps,
  uiTypeIs,
} from '@jsonforms/core';
import { useJsonForms, withJsonFormsLayoutProps } from '@jsonforms/react';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import './SpectrumTableViewRenderer.css';
/**
 * Default tester for a label.
 * @type {RankedTester}
 */
export const spectrumTableViewRendererTester: RankedTester = rankWith(
  1,
  uiTypeIs('TableView')
);

/**
 * Default renderer for a label.
 */
export const SpectrumTableViewRenderer: FunctionComponent<RendererProps> = ({
  uischema,
  visible,
}) => {
  const tableRef = useRef(null);
  let staticData = uischema.options?.staticData;
  const columns = uischema.options?.columns;
  const rows = uischema.options?.rows;
  const spectrumProps = uischema.options?.spectrumProps;
  const fixedNumRows = uischema.options?.fixedNumRows;
  const fixedHeight = uischema.options?.fixedHeight;
  const noContentMessage = uischema.options?.noContentMessage;
  const noContentStyle = uischema.options?.noContentStyle;

  const [columnsData, setColumnsData] = useState<
    { name: string; uid: string }[]
  >([]);
  const context = useJsonForms();
  let [rowsData, setRowsData] = useState<{ [key: string]: string }[]>([]);
  const tableViewCallback =
    context?.config?.lifecycleHooks?.onSuccessCallbacks?.tableView;
  const onRowsDataChange = tableViewCallback?.onRowsDataChange;

  if (onRowsDataChange) {
    onRowsDataChange({ rowsData, setRowsData });
  }
  useEffect(() => {
    if (staticData) {
      setColumnsData(columns.data);
      setRowsData(rows.data);
    } else {
      fetch(columns.options?.baseURL, {
        method: 'GET',
      })
        .then((res) => {
          return res.json();
        })
        .then((data) => {
          setColumnsData(data);
        });

      fetch(rows.options?.baseURL, {
        method: 'GET',
      })
        .then((res) => {
          return res.json();
        })
        .then((data) => {
          setRowsData(data);
        });
    }
  }, []);
  const noOfrows = uischema.options?.noOfRows || rowsData.length;

  const className = uischema.options?.className;
  let tableFinalHeight = 'auto';
  if (fixedNumRows && noOfrows > fixedNumRows) {
    tableFinalHeight = fixedHeight;
  }

  return (
    <div
      ref={tableRef}
      aria-label='table-view'
      className='tableWidth'
      hidden={visible === undefined || visible === null ? false : !visible}
    >
      {columnsData.length > 0 && rowsData.length > 0 && (
        <TableView
          UNSAFE_style={{ height: tableFinalHeight }}
          {...spectrumProps}
          UNSAFE_className={className}
          aria-label='Table'
        >
          <TableHeader columns={columnsData}>
            {(column: { name: string; uid: string; minWidth: string }) => (
              <Column key={column.uid} minWidth={column.minWidth as any}>
                {column.name}
              </Column>
            )}
          </TableHeader>
          <TableBody items={rowsData}>
            {(item: { [key: string]: string }) => (
              <Row>{(columnKey: string) => <Cell>{item[columnKey]}</Cell>}</Row>
            )}
          </TableBody>
        </TableView>
      )}

      {(!columnsData.length || !rowsData.length) && (
        <Flex alignItems='center' alignContent='center'>
          <View alignSelf='center'>
            {noContentMessage ? (
              <div
                dangerouslySetInnerHTML={{ __html: noContentMessage }}
                style={{ ...noContentStyle }}
              ></div>
            ) : (
              <ProgressCircle aria-label='Loading…' isIndeterminate />
            )}
          </View>
        </Flex>
      )}
    </div>
  );
};

export default withJsonFormsLayoutProps(SpectrumTableViewRenderer);
